// Login screen — paper-warm mobile-first per mockup/MOBILE.md §3 + W2 §4.2.
// Real wiring (W2 §3.2 #12): dispatches on `error.code` for field routing.

import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Info, Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/AuthShell";
import { FormErrorBanner, FormField } from "@/components/auth/FormField";
import { InfoBanner } from "@/components/primitives/InfoBanner";
import { Button } from "@/components/ui/button";
import { ApiError, login } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth/AuthProvider";
import { AUTH_I18N, authLockoutMessage } from "@/lib/i18n/auth";

interface FieldErrors {
  identifier?: string;
  password?: string;
  form?: string;
}

export function LoginPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const next = params.get("next") || "/dashboard";
  // Set by `RequireAuth` when this device *had* a session and lost it without
  // asking — which in this app means "you signed in somewhere else", because
  // w2.md §4.7 makes login revoke other sessions. A first-time visitor arrives
  // here without the parameter and sees nothing, which is correct: there is
  // nothing to explain.
  const sessionEnded = params.get("reason") === "session-expired";

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await login({ identifier: identifier.trim(), password });
      await refresh();
      nav(next.startsWith("/") ? next : "/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(mapLoginError(err));
      } else {
        setErrors({ form: "登入時發生錯誤, 請稍後再試" });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="登入"
      subtitle="歡迎回來。請輸入帳戶資料繼續。"
      backHref="/"
      footer={
        <>
          還沒有帳戶?{" "}
          <Link
            to={`/register${next && next !== "/dashboard" ? `?next=${encodeURIComponent(next)}` : ""}`}
            className="text-you-strong font-medium"
            data-testid="link-register"
          >
            建立一個
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {sessionEnded ? (
          <div data-testid="login-session-ended" role="status">
            <InfoBanner tone="paper-deep" icon={<Info aria-hidden />}>
              {AUTH_I18N.session_expired}
            </InfoBanner>
          </div>
        ) : null}
        <FormErrorBanner message={errors.form} />
        <FormField
          name="identifier"
          label="帳戶"
          required
          hint="使用 email 或 username"
          autoComplete="username"
          value={identifier}
          onChange={setIdentifier}
          invalid={Boolean(errors.identifier)}
          errorMessage={errors.identifier}
          autoFocus
          disabled={busy}
        />
        <FormField
          name="password"
          label="密碼"
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
          invalid={Boolean(errors.password)}
          errorMessage={errors.password}
          disabled={busy}
        />
        <Button
          type="submit"
          variant="primary"
          disabled={busy}
          data-testid="submit-login"
          leadingIcon={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        >
          {busy ? "登入中…" : "登入"}
        </Button>
        <Link
          to="/forgot-password"
          className="text-body-sm text-ink-3 self-end hover:text-ink-2"
          data-testid="link-forgot"
        >
          忘記密碼?
        </Link>
      </form>
    </AuthShell>
  );
}

function mapLoginError(err: ApiError): FieldErrors {
  // Per W2 §3.2 #8 fine-grained codes; #28 dispatch on error.code.
  switch (err.code) {
    case "AUTH_INVALID_CREDENTIALS":
      return { form: "帳戶或密碼不正確, 請重試" };
    case "AUTH_ACCOUNT_LOCKED": {
      // The `Retry-After` header is the truth: the api layer reads it into
      // `ApiError.retry_after_seconds` (`lib/api/pair.ts` — `apiError`).
      // W54.0 C1 made the lockout window a setting, so the old hard-coded
      // `900` fallback would have told people to wait 15 minutes for a
      // 60-second lock. The copy lives in `lib/i18n/auth.ts` with the rest of
      // the auth strings.
      const ra = (err as unknown as { retry_after_seconds?: number }).retry_after_seconds;
      return { form: authLockoutMessage(ra) };
    }
    case "VALIDATION_FAILED":
      return { form: err.message || "欄位驗證失敗" };
    default:
      return { form: err.message || "登入失敗" };
  }
}
